import LZString from 'lz-string'
import { str2uia, verifySignature } from './crypto'

export interface NfcCallback {
    onCodes: (codes: string[], serialNumber: string) => void
    onError?: (error: unknown) => void
}

export interface NfcHandle {
    stop: () => void
}

interface Header {
    bz: number
    mail: string
    sig: string
}

function decompressFromUint8Array(input: Uint8Array): number[] {
    if (input.length === 0) return []
    const decoded = LZString.decompressFromUint8Array(input)
    if (!decoded) return []
    const arr = decoded.split(',').map(Number)
    for (let i = 1; i < arr.length; i++) {
        arr[i] += arr[i - 1]
    }
    return arr
}

export async function startNfcReader(callback: NfcCallback): Promise<NfcHandle> {
    if (!('NDEFReader' in window) || !window.NDEFReader) {
        throw new Error('NFC not available')
    }

    const reader = new window.NDEFReader()
    const controller = new AbortController()

    await reader.scan({ signal: controller.signal })

    reader.onreadingerror = () => {
        callback.onError?.(new Error('Cannot read NFC tag'))
    }

    reader.onreading = event => {
        void handleReading(event)
    }

    async function handleReading(event: NDEFReadingEvent) {
        try {
            const records = event.message.records
            if (records.length === 0) {
                callback.onError?.(new Error('No NFC records'))
                return
            }

            let header: Header | null = null
            const codes: string[] = []

            for (const record of records) {
                if (record.recordType !== 'mime' || !record.data) continue

                if (record.mediaType === 'application/json') {
                    const decoder = record.encoding ? new TextDecoder(record.encoding) : new TextDecoder()
                    const text = decoder.decode(record.data)
                    const parsed = JSON.parse(text) as Header
                    if (parsed.bz !== 1) throw new Error(`Unsupported version: ${parsed.bz}`)
                    if (!parsed.mail) throw new Error('Missing mail')
                    if (!parsed.sig) throw new Error('Missing signature')
                    header = parsed
                } else if (record.mediaType === 'application/octet-stream') {
                    if (!header) throw new Error('Header missing — cannot verify')
                    const compressed = new Uint8Array(record.data.buffer)
                    const dataToVerify = new Uint8Array([
                        ...str2uia(event.serialNumber),
                        ...str2uia(header.mail),
                        ...compressed
                    ])
                    const ok = await verifySignature(str2uia(header.sig), dataToVerify)
                    if (!ok) throw new Error('Signature mismatch')
                    const decompressed = decompressFromUint8Array(compressed)
                    codes.push(...decompressed.map(String))
                }
            }

            if (codes.length > 0) {
                callback.onCodes(codes, event.serialNumber)
            }
        } catch (err) {
            callback.onError?.(err)
        }
    }

    return {
        stop: () => controller.abort()
    }
}
